<template>
  <div class="signin-container">
    <div
      v-loading="wait.is('USER_SIGNIN_WAIT')"
      class="signin-card"
    >
      <div class="signin-card-inner">
        <div class="signin-logo">
          <img :src="logoSrc">
          <div class="voice-analytics-title">
            {{ $t('vaApp.title') }}
          </div>
        </div>
        <div class="signin-form">
          <form @submit.prevent="signin">
            <div class="mb-200">
              <label for="email" />
              <BInput
                v-model="email"
                v-model:valid="emailValid"
                :placeholder="$t('auth.email')"
                color="focus"
                data-test="email"
                input-id="email"
              />
            </div>
            <div class="mb-600">
              <label for="password" />
              <BInput
                v-model="password"
                v-model:valid="passWordValid"
                type="password"
                name="password"
                :placeholder="$t('auth.password')"
                color="focus"
                data-test="password"
                input-id="password"
                show-password
                @keypress-enter="signin"
              />
            </div>
            <BLayout
              class="mb-300"
              justify-center
            >
              <BBtn
                class="signin-btn"
                type="primary"
                data-test="signinBtn"
                btn-type="submit"
                :disabled="!emailValid || !passWordValid"
                @click.prevent="signin"
              >
                <span>{{ $t('auth.signin') }}</span>
              </BBtn>
            </BLayout>
          </form>
          <BLayout
            align-center
            justify-center
          >
            <BBtn text>
              <RouterLink :to="{ name: 'VaAppResetPassword' }">
                {{ $t('auth.resetPassword') }}
              </RouterLink>
            </BBtn>
          </BLayout>
        </div>
      </div>
      <div class="signin-card-footer">
        <a
          href="https://smartcamp.co.jp/privacy_policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ $t('org.privacyPolicy') }}
        </a>
        <a
          href="https://balescloud.jp/tos"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ $t('org.termsOfUse') }}
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import logoSrc from '@/assets/brand_logo.svg';
import { useConfirmWithOptions } from '@/composable/message-dialog';
import { useWait } from '@/composable/vue-wait';
import UserService from '@/services/user_service';

const userService = new UserService();

const { wait, doActionWithWait } = useWait();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const { confirmWithOptions } = useConfirmWithOptions();

const email = ref('');
const password = ref('');
const emailValid = ref(false);
const passWordValid = ref(false);

const signin = async () => {
  doActionWithWait('USER_SIGNIN_WAIT', async () => {
    try {
      await userService.signinUser(email.value, password.value, 'voice_analytics');
      const destLocation = route.query.dest_location;
      const destination = destLocation
        ? Array.isArray(destLocation) ? destLocation[0] : destLocation
        : { name: 'VaCallList' };
      router.push(destination);
    } catch (error) {
      let bitterAlertText = error.message;
      switch (error.code) {
        case 'auth/invalid-credential':
        case 'auth/invalid-email':
        case 'auth/wrong-password':
        case 'auth/user-not-found':
          bitterAlertText = t('auth.errorMessage.wrongEmailOrPassword');
          break;
        case 'auth/user-disabled':
          bitterAlertText = t('auth.errorMessage.userDisabled');
          break;
        case 'auth/too-many-requests':
          bitterAlertText = t('auth.errorMessage.tooManyRequests');
          break;
        default:
          if (error.response && error.response.status === 403) {
            bitterAlertText = t('auth.error.clientExpired');
          } else if (error.response && error.response.status === 404) {
            bitterAlertText = t('auth.errorMessage.wrongEmailOrSlug');
          }
      }
      await confirmWithOptions({
        title: t('auth.error.signinError'),
        message: bitterAlertText,
        closeOnClickOutside: true,
        optionalButtonOptions: {
          confirm: {
            text: t('general.ok'),
          },
        },
      });
    }
  });
};
</script>

<style lang="scss" scoped>
.signin-container {
  background:
    linear-gradient(0deg, rgba(18, 76, 73, 0.30) 0%, rgba(18, 76, 73, 0.30) 100%),
    url('@/assets/signin-background_2.png');
  background-repeat: no-repeat;
  background-attachment: fixed;
  background-size: cover;

  min-height: 100vh;

  .signin-card {
    display: flex;
    flex-direction: column;
    justify-content: center;
    background:  #EAF9F8;
    width: 480px;
    min-height: 100vh;
    padding-top: 60px;
    margin: 0 auto;
    position: relative;
    box-shadow: $boxshadow-base;

    &-inner {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      width: 280px;
      margin: 0 auto;
    }
    &-footer {
      text-align: center;
      padding: $basespace-700 0;
      a {
        color: $textcolor-light;
        text-decoration: underline;
      }
      a:not(:first-child) {
        margin-left: $basespace-400;
      }
    }
  }
}

.signin-logo {
  margin: $basespace-800 auto 60px auto;

  img {
    width: 100%;
    height: 60px;
  }

  .voice-analytics-title {
    padding: 8px 24px;
    border-radius: 32px;
    background: $basecolor-primary;
    color: $textcolor-white;
    font-size: 28px;
    font-weight: 500;
    margin-inline: auto;
    margin-top: 12px;
    width: fit-content;
  }
}

.signin-btn {
  width: 100%;
  margin-left: $basespace-400;
  margin-right: $basespace-400;
}
</style>
